import { useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  Text,
  InputSelectControl,
  InputSelectControlField,
  Primary13Regular,
  RedRed60
} from '@sequencehq/core-components'
import { getOptionsFromTaxCategories } from '@sequencehq/utils'

type ValidationError = { message: string }

type Props = {
  taxCategoryId: string
  setTaxCategoryId: (id: string) => void
  /** Pass to show inline error message below the field. */
  validationErrors?: ValidationError[]
  /** Pass when the parent handles error rendering (e.g. FieldLabel). */
  isInvalid?: boolean
  /** Pass to render the field's own label. Omit when a parent (e.g.
   *  FieldLabel) provides the label. */
  label?: string
  'data-testid'?: string
}

export const TaxCategoryField = ({
  taxCategoryId,
  setTaxCategoryId,
  validationErrors,
  isInvalid,
  label,
  'data-testid': dataTestId
}: Props) => {
  const { data, isLoading, isFetching } = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxCategories
  )

  const taxCategories = data?.pages?.flatMap(page => page.items) ?? []
  const taxCategoriesOptions = getOptionsFromTaxCategories(taxCategories)
  const noTaxCategories = !isLoading && taxCategories.length === 0
  const hasError = Boolean(validationErrors?.length) || Boolean(isInvalid)
  const errorMessage = validationErrors?.at(0)?.message

  // The selected label can go blank in two scenarios:
  //   1. Initial load (isLoading=true): options=[], ID can't resolve to a label.
  //   2. Background re-fetch after cache invalidation (isFetching=true, options
  //      momentarily empty): same result, blank trigger.
  // In both cases, if a taxCategoryId is already set we show '...' so the
  // field never appears empty while data is in-flight.
  const optionsReady = taxCategoriesOptions.some(
    opt => opt.value === taxCategoryId
  )
  const overrideSelectedLabel =
    taxCategoryId && !optionsReady && (isLoading || isFetching)
      ? '...'
      : undefined

  const controlProps = {
    'data-testid': dataTestId,
    options: taxCategoriesOptions,
    value: taxCategoryId,
    onChange: (newValue: string) => setTaxCategoryId(newValue),
    placeholder: noTaxCategories ? 'No tax categories' : 'Select tax category',
    disabled: isLoading || noTaxCategories,
    overrideSelectedLabel
  }

  // When a label is supplied the field owns its label + error rendering;
  // otherwise it stays a bare control whose parent (e.g. FieldLabel) provides
  // the label — keeping existing call sites unchanged.
  if (label !== undefined) {
    return (
      <InputSelectControlField
        {...controlProps}
        label={label}
        validationErrors={validationErrors}
      />
    )
  }

  return (
    <>
      <InputSelectControl {...controlProps} isInvalid={hasError} />
      {errorMessage && (
        <Text {...Primary13Regular} color={RedRed60} mt="8px">
          {errorMessage}
        </Text>
      )}
    </>
  )
}
